import { useState } from "react";
import { LoaderIcon } from "lucide-react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useRedactarDesdeTerreno, useResolverPropuesta } from "@/hooks/useAsistenciaDeRedaccion";
import { getApiErrorMessage, parseValidationErrors } from "@/lib/api";
import type { CampoRedactableDesdeTerreno } from "@/lib/ia";

interface RedactarDesdeTerrenoProps {
  informeId: number;
  campo: CampoRedactableDesdeTerreno;
  /** Lo que el campo tiene hoy: con texto, se pide confirmación antes de reemplazarlo. */
  valorActual: string;
  /** Clases extra del botón (el diseño de suelos lo dibuja más compacto). */
  claseDelBoton?: string;
  /** Recibe el texto propuesto. Quien llama lo pone en el formulario (sucio) y desmarca la validación. */
  onRedactado: (texto: string) => void;
}

/**
 * "Redactar desde terreno": pide al servidor que redacte la descripción con lo
 * que el encargado mandó (textos y notas de voz transcritas).
 *
 * ES UNA PROPUESTA QUE ENTRA AL FORMULARIO, NO UN GUARDADO. El texto queda como
 * cambio sin guardar y la casilla "Información validada" se desmarca (lo hace
 * quien llama con `onRedactado`): lo escribió un modelo y una persona tiene que
 * revisarlo. Si el campo ya tenía texto se pregunta antes — a diferencia de la
 * asistencia de redacción, acá el resultado SÍ reemplaza lo escrito.
 *
 * Solo se dibuja cuando el campo tiene material (un texto o una transcripción
 * lista): los casos en que el servidor respondería 422 no se ofrecen.
 */
export function RedactarDesdeTerreno({
  informeId,
  campo,
  valorActual,
  claseDelBoton,
  onRedactado,
}: RedactarDesdeTerrenoProps) {
  const redactar = useRedactarDesdeTerreno(informeId);
  const resolver = useResolverPropuesta();
  const [confirmando, setConfirmando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function pedir() {
    setError(null);
    redactar.mutate(campo, {
      onSuccess: ({ ejecucion_id, texto }) => {
        onRedactado(texto);
        toast.success("Texto redactado desde terreno. Revísalo antes de marcarlo como revisado.");
        // El texto ya entró al formulario. Si el registro falla no se le avisa
        // a nadie: la persona ya obtuvo lo que quería (ver CampoConAsistencia).
        resolver.mutate({ ejecucionId: ejecucion_id, resultado: "aceptado" });
      },
      onError: (e) =>
        setError(
          parseValidationErrors(e)?.terreno ??
            getApiErrorMessage(e, "No pudimos redactar el texto. Inténtalo de nuevo."),
        ),
    });
  }

  return (
    <div className="space-y-2">
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className={claseDelBoton}
        disabled={redactar.isPending}
        onClick={() => (valorActual.trim() === "" ? pedir() : setConfirmando(true))}
      >
        {redactar.isPending ? <Spinner /> : <LoaderIcon aria-hidden="true" strokeWidth={1.8} />}
        {redactar.isPending ? "Redactando…" : "Redactar desde terreno"}
      </Button>

      <div aria-live="polite">
        {error ? <p className="text-destructive text-xs font-medium">{error}</p> : null}
      </div>

      <AlertDialog open={confirmando} onOpenChange={setConfirmando}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Reemplazar el texto actual?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta descripción ya tiene texto. El nuevo lo reemplaza en el formulario, pero no se
              guarda hasta que presiones «Guardar cambios».
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={pedir}>Reemplazar el texto</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
